import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';

import { SupportTicketService, SupportTicket, TicketFilters, TicketStats } from '../../../shared/services/support-ticket.service';
import { ApiService } from '../../../shared/services/api.service';

@Component({
  selector: 'app-support-ticket',
  templateUrl: './support-ticket.component.html',
  styleUrls: ['./support-ticket.component.css'],
})
export class SupportTicketComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  // Form related
  ticketForm!: FormGroup;
  mode: 'add' | 'edit' | 'view' = 'add';
  resourceId: string | null = null;
  isViewMode = false;
  
  // Data
  categories: any[] = [];
  priorities: any[] = [];
  statuses: any[] = [];
  loading = false;

  // List related
  tickets: SupportTicket[] = [];
  filteredTickets: SupportTicket[] = [];
  displayedColumns: string[] = ['ticket_number', 'name', 'category', 'department', 'priority', 'status', 'created_at', 'actions'];
  
  // Pagination
  currentPage = 1;
  totalPages = 0;
  totalRecords = 0;
  pageSize = 10;
  pageSizeOptions = [5, 10, 25, 50];

  // Filters
  filters: TicketFilters = {
    page: 1,
    limit: 10,
    sort_by: 'created_at',
    sort_order: 'DESC'
  };

  // Filter options
  priorityOptions = ['low', 'medium', 'high', 'critical'];
  statusOptions = ['open', 'in_progress', 'pending', 'resolved', 'closed'];

  // Stats
  stats: TicketStats | null = null;

  // View modes
  viewMode: 'list' | 'form' = 'list';
  selectedTicket: SupportTicket | null = null;

  // User role and access control
  userD: any;
  isAdmin = false;
  isSupportPerson = false;
  isRegularUser = false;
  currentUserId: number | null = null;
  staffList: any[] = [];
  isLoadingStaff = false;

  constructor(
    private route: ActivatedRoute, 
    private router: Router,
    private fb: FormBuilder,
    private supportTicketService: SupportTicketService,
    private snackBar: MatSnackBar,
    public dialog: MatDialog,
    private apiService: ApiService
  ) {
    this.checkUserRole();
    this.initForm();
  }

  ngOnInit(): void {
    this.loadInitialData();
    this.loadTickets();
    this.loadStaffList();
    // this.loadStats();
  }

  checkUserRole(): void {
    try {
      const user = localStorage.getItem('user');
      if (user) {
        this.userD = JSON.parse(user);
        const userType = this.userD?.user?.type || this.userD?.type;
        const roleId = this.userD?.user?.role_id || this.userD?.role_id;
        this.currentUserId = this.userD?.user?.id || this.userD?.id;

        // Check if user is admin (type 2 = organization admin, role_id 1 = superadmin)
        this.isAdmin = userType === 2 || roleId === 1 || userType === 1;
        
        // Regular user (type 4 = regular user)
        this.isRegularUser = userType === 4;
        
        // Support person will be determined when viewing/editing a ticket
        // (if assigned_user_id matches current user)
      }
    } catch (error) {
      console.error('Error checking user role:', error);
    }
  }

  loadStaffList(): void {
    if (this.isAdmin) {
      this.isLoadingStaff = true;
      const params = { keyword: "" };
      this.apiService.getStaff(params).subscribe({
        next: (res: any) => {
          this.staffList = res.data || [];
          this.isLoadingStaff = false;
        },
        error: (err) => {
          console.error('Error fetching staff:', err);
          this.isLoadingStaff = false;
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ==================== INITIALIZATION ====================

  loadInitialData(): void {
    // Load categories, priorities, and statuses
    Promise.all([
      this.supportTicketService.getCategories().toPromise(),
      this.supportTicketService.getPriorities().toPromise(),
      this.supportTicketService.getStatuses().toPromise()
    ]).then(([categoriesRes, prioritiesRes, statusesRes]) => {
      if (categoriesRes?.data) this.categories = categoriesRes.data;
      if (prioritiesRes?.data) this.priorities = prioritiesRes.data;
      if (statusesRes?.data) this.statuses = statusesRes.data;
    }).catch(error => {
      console.error('Error loading initial data:', error);
    });
  }

  initForm(): void {
    // For regular users, only basic fields are required
    // Technician is not required for regular users when creating tickets
    const isBasicForm = this.isRegularUser && !this.isAdmin && !this.isSupportPerson;
    
    this.ticketForm = this.fb.group({
      ticket_number: [{ value: '', disabled: true }],
      ticket_category: ['', Validators.required],
      name: ['', Validators.required],
      job_title: [''],
      phone: [''],
      manager: [''],
      date_time: [new Date().toISOString().slice(0, 16), Validators.required],
      id_number: ['', Validators.required],
      department: ['', Validators.required],
      technician: ['none'], // Default value, not required for regular users
      problem_description: ['', Validators.required],
      assigned_to: ['none'],
      assigned_user_id: [null], // For admin assignment
      response_date: [''],
      response_time: [''],
      total_hours_worked: [0],
      initial_action: [''],
      follow_up_action: [''],
      importance_mission_critical: [false],
      importance_slowing_user_down: [false],
      importance_to_be_scheduled: [false],
      conclusion_resolved: [false],
      conclusion_pending: [false],
      conclusion_unresolved: [false],
      final_notes: [''],
      status: ['open'],
      priority: ['medium']
    });
  }

  // ==================== LIST OPERATIONS ====================

  loadTickets(): void {
    this.loading = true;
    this.supportTicketService.getTickets(this.filters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.tickets = response.data.tickets;
          this.filteredTickets = this.tickets;
          this.currentPage = response.data.pagination.current_page;
          this.totalPages = response.data.pagination.total_pages;
          this.totalRecords = response.data.pagination.total_records;
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading tickets:', error);
          this.showError('Failed to load support tickets');
          this.loading = false;
        }
      });
  }

  loadStats(): void {
    this.supportTicketService.getTicketStats()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.stats = response.data;
        },
        error: (error) => {
          console.error('Error loading stats:', error);
        }
      });
  }

  onPageChange(event: any): void {
    this.filters.page = event.pageIndex + 1;
    this.filters.limit = event.pageSize;
    this.pageSize = event.pageSize;
    this.loadTickets();
  }

  applyFilters(): void {
    this.filters.page = 1;
    this.loadTickets();
  }

  clearFilters(): void {
    this.filters = {
      page: 1,
      limit: this.pageSize,
      sort_by: 'created_at',
      sort_order: 'DESC'
    };
    this.loadTickets();
  }

  onSearchChange(event: any): void {
    const searchTerm = event.target.value;
    if (searchTerm) {
      this.filters.search = searchTerm;
    } else {
      delete this.filters.search;
    }
    this.applyFilters();
  }

  // ==================== CRUD OPERATIONS ====================

  showCreateForm(): void {
    this.viewMode = 'form';
    this.mode = 'add';
    this.isSupportPerson = false; // Reset when creating new ticket
    this.resetForm();
    
    // If regular user, restrict form fields
    if (this.isRegularUser && !this.isAdmin) {
      this.restrictFormForRegularUser();
    }
  }

  showList(): void {
    this.viewMode = 'list';
    this.mode = 'add';
    this.resetForm();
  }

  viewTicket(ticket: SupportTicket): void {
    this.selectedTicket = ticket;
    this.mode = 'view';
    this.viewMode = 'form';
    this.patchFormWithTicketData(ticket);
    this.ticketForm.disable();
  }

  editTicket(ticket: SupportTicket): void {
    this.selectedTicket = ticket;
    this.mode = 'edit';
    this.viewMode = 'form';
    
    this.patchFormWithTicketData(ticket);
    this.ticketForm.enable();
    // Keep ticket_number disabled
    this.ticketForm.get('ticket_number')?.disable();
    
    // If regular user editing their own ticket, restrict fields
    if (this.isRegularUser && !this.isAdmin && !this.isSupportPerson) {
      this.restrictFormForRegularUser();
    }
  }

  restrictFormForRegularUser(): void {
    // Disable admin-only fields for regular users
    const adminOnlyFields = [
      'assigned_to', 'assigned_user_id', 'response_date', 'response_time',
      'total_hours_worked', 'initial_action', 'follow_up_action',
      'importance_mission_critical', 'importance_slowing_user_down',
      'importance_to_be_scheduled', 'conclusion_resolved', 'conclusion_pending',
      'conclusion_unresolved', 'final_notes', 'status', 'technician'
    ];
    
    adminOnlyFields.forEach(field => {
      this.ticketForm.get(field)?.disable();
    });
  }

  deleteTicket(ticket: SupportTicket): void {
    if (confirm(`Are you sure you want to delete ticket ${ticket.ticket_number}?`)) {
      this.loading = true;
      this.supportTicketService.deleteTicket(ticket.id!)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            this.showSuccess('Ticket deleted successfully');
            this.loadTickets();
            this.loadStats();
            this.loading = false;
          },
          error: (error) => {
            console.error('Error deleting ticket:', error);
            this.showError('Failed to delete ticket');
            this.loading = false;
          }
        });
    }
  }

  updateTicketStatus(ticket: SupportTicket, newStatus: string): void {
    this.supportTicketService.updateTicket(ticket.id!, { status: newStatus })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.showSuccess(`Ticket status updated to ${this.getStatusText(newStatus)}`);
          this.loadTickets();
          this.loadStats();
        },
        error: (error) => {
          console.error('Error updating ticket status:', error);
          this.showError('Failed to update ticket status');
        }
      });
  }

  // ==================== FORM OPERATIONS ====================

  patchFormWithTicketData(ticket: SupportTicket): void {
    // Check if current user is the assigned support person
    this.isSupportPerson = ticket.assigned_user_id === this.currentUserId;
    
    // Map API data to form structure
    const formData = {
      ticket_number: ticket.ticket_number,
      ticket_category: ticket.ticket_category,
      name: ticket.name,
      job_title: ticket.job_title || '',
      phone: ticket.phone || '',
      manager: ticket.manager || '',
      date_time: ticket.date_time ? new Date(ticket.date_time).toISOString().slice(0, 16) : '',
      id_number: ticket.id_number,
      department: ticket.department,
      technician: ticket.technician || '',
      problem_description: ticket.problem_description,
      assigned_to: ticket.assigned_to || '',
      assigned_user_id: ticket.assigned_user_id || null,
      response_date: ticket.response_date || '',
      response_time: ticket.response_time || '',
      total_hours_worked: ticket.total_hours_worked || 0,
      initial_action: ticket.initial_action || '',
      follow_up_action: ticket.follow_up_action || '',
      importance_mission_critical: ticket.importance_mission_critical || false,
      importance_slowing_user_down: ticket.importance_slowing_user_down || false,
      importance_to_be_scheduled: ticket.importance_to_be_scheduled || false,
      conclusion_resolved: ticket.conclusion_resolved || false,
      conclusion_pending: ticket.conclusion_pending || false,
      conclusion_unresolved: ticket.conclusion_unresolved || false,
      final_notes: ticket.final_notes || '',
      status: ticket.status || 'open',
      priority: ticket.priority || 'medium'
    };

    this.ticketForm.patchValue(formData);
  }

  onSubmit(): void {
    if (this.ticketForm.valid) {
      this.loading = true;
      const formValue = this.ticketForm.getRawValue(); // Use getRawValue to include disabled fields
      
      // Map form data to API structure
      const ticketData: SupportTicket = {
        ticket_number: formValue.ticket_number,
        ticket_category: formValue.ticket_category,
        name: formValue.name,
        job_title: formValue.job_title,
        phone: formValue.phone,
        manager: formValue.manager,
        date_time: formValue.date_time,
        id_number: formValue.id_number,
        department: formValue.department,
        technician: formValue.technician || 'none', // Default for regular users
        problem_description: formValue.problem_description,
        assigned_to: formValue.assigned_to || 'none',
        assigned_user_id: formValue.assigned_user_id || undefined,
        response_date: formValue.response_date,
        response_time: formValue.response_time,
        total_hours_worked: formValue.total_hours_worked,
        initial_action: formValue.initial_action,
        follow_up_action: formValue.follow_up_action,
        importance_mission_critical: formValue.importance_mission_critical,
        importance_slowing_user_down: formValue.importance_slowing_user_down,
        importance_to_be_scheduled: formValue.importance_to_be_scheduled,
        conclusion_resolved: formValue.conclusion_resolved,
        conclusion_pending: formValue.conclusion_pending,
        conclusion_unresolved: formValue.conclusion_unresolved,
        final_notes: formValue.final_notes,
        status: formValue.status,
        priority: formValue.priority
      };
      
      if (this.mode === 'add') {
        this.supportTicketService.createTicket(ticketData)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              this.showSuccess('Ticket created successfully');
              this.loading = false;
              this.loadTickets();
              this.loadStats();
              this.showList();
            },
            error: (error) => {
              console.error('Error creating ticket:', error);
              this.showError('Failed to create ticket');
              this.loading = false;
            }
          });
      } else if (this.mode === 'edit' && this.selectedTicket) {
        this.supportTicketService.updateTicket(this.selectedTicket.id!, ticketData)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              this.showSuccess('Ticket updated successfully');
              this.loading = false;
              this.loadTickets();
              this.loadStats();
              this.showList();
            },
            error: (error) => {
              console.error('Error updating ticket:', error);
              this.showError('Failed to update ticket');
              this.loading = false;
            }
          });
      }
    } else {
      this.ticketForm.markAllAsTouched();
      this.showError('Please fill in all required fields');
    }
  }

  onCancel(): void {
    this.showList();
  }

  resetForm(): void {
    this.ticketForm.reset({
      status: 'open',
      priority: 'medium',
      date_time: new Date().toISOString().slice(0, 16),
      total_hours_worked: 0,
      importance_mission_critical: false,
      importance_slowing_user_down: false,
      importance_to_be_scheduled: false,
      conclusion_resolved: false,
      conclusion_pending: false,
      conclusion_unresolved: false
    });
    this.selectedTicket = null;
    this.mode = 'add';
    this.isSupportPerson = false; // Reset support person flag
    this.ticketForm.enable();
    this.ticketForm.get('ticket_number')?.disable();
    
    // Re-apply restrictions if regular user
    if (this.isRegularUser && !this.isAdmin) {
      this.restrictFormForRegularUser();
    }
  }

  // ==================== UTILITY METHODS ====================

  getStatusColor(status: string): string {
    return this.supportTicketService.getStatusColor(status);
  }

  getPriorityColor(priority: string): string {
    return this.supportTicketService.getPriorityColor(priority);
  }

  getStatusText(status: string): string {
    return this.supportTicketService.getStatusText(status);
  }

  getPriorityText(priority: string): string {
    return this.supportTicketService.getPriorityText(priority);
  }

  formatDate(dateString: string): string {
    return this.supportTicketService.formatDate(dateString);
  }

  getTimeAgo(dateString: string): string {
    return this.supportTicketService.getTimeAgo(dateString);
  }

  private showSuccess(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 3000,
      panelClass: ['success-snackbar']
    });
  }

  private showError(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 5000,
      panelClass: ['error-snackbar']
    });
  }

  // Helper methods for template visibility
  showAdminFields(): boolean {
    return this.isAdmin || this.isSupportPerson;
  }

  showAssignmentFields(): boolean {
    return this.isAdmin;
  }

  showSupportPersonFields(): boolean {
    return this.isAdmin || this.isSupportPerson;
  }
}
